/**
 * captureCredentials.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import React from "react";

import { Alert, Button, FormControl, TextField } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { Brand } from "../models/brand";
import { EMAIL_REGEX } from "../models/auth";
import { authServiceFactory } from "../services/auth.service";

export function CaptureCredentialsComponent(): React.ReactElement<any, any> {
  const authContext = React.useContext(AuthContext);
  const [email, setEmail] = React.useState<string>("");
  const [emailErr, setEmailErr] = React.useState<string>("");
  const [pass, setPass] = React.useState<string>("");
  const [passErr, setPassErr] = React.useState<string>("");
  const [confirmpass, setConfirmPass] = React.useState<string>("");
  const [confirmErr, setConfirmErr] = React.useState<string>("");
  const [finished, setFinished] = React.useState<boolean>(false);
  const [saveErr, setSaveErr] = React.useState<string>("");
  const passRef = React.useRef<HTMLInputElement>(null);
  const confirmRef = React.useRef<HTMLInputElement>(null);

  const productName = authContext.brand === Brand.GLYPH ? "GLYPH" : "Mercury Workspace";

  async function doSaveCredentials() {
    try {
      let hasErr = false;
      if (!email) {
        setEmailErr("Enter an email address.");
        hasErr = true;
      } else if (!EMAIL_REGEX.test(email)) {
        setEmailErr("Invalid email address.");
        hasErr = true;
      } else {
        setEmailErr("");
      }
      if (pass) {
        const msg = authServiceFactory().checkPWRequirements(pass);
        if (msg) {
          setPassErr(msg);
          hasErr = true;
        } else {
          setPassErr("");
        }
      } else {
        setPassErr("Enter a password.");
        hasErr = true;
      }
      if (confirmpass && confirmpass !== pass) {
        setConfirmErr("Password mismatch.");
        hasErr = true;
      } else if (!confirmpass) {
        setConfirmErr("Re-enter your password.");
        hasErr = true;
      } else {
        setConfirmErr("");
      }
      if (hasErr) { return; }

      const authService = authServiceFactory();
      await authService.addEmail(email);
      await authService.changePassword("", pass);

      setFinished(true);
    } catch (err) {
      console.error("Error saving credentials:", err);
      setSaveErr((err as Error).message);
    }
  }

  function updateValue (event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    const newValue = inputElem.value;
    let msg = "";

    switch (propName) {
      case "email":
        setEmail(newValue);
        if (!EMAIL_REGEX.test(newValue)) {
          setEmailErr("Invalid email address.");
        }
        break;
      case "pass":
        setPass(newValue);
        msg = authServiceFactory().checkPWRequirements(newValue);
        if (msg) {
          setPassErr(msg);
        } else {
          setPassErr("");
        }
        break;
      case "confirmpass":
        setConfirmPass(newValue);
        if (newValue !== pass) {
          setConfirmErr("Password mismatch.");
        } else {
          setConfirmErr("");
        }
        break;
    }
  }

  function onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    switch (propName) {
      case "email":
        if (!email) {
          setEmailErr("Enter an email address.");
        } else if (!EMAIL_REGEX.test(email)) {
          setEmailErr("Invalid email address.");
        } else {
          setEmailErr("");
        }
        break;
      case "pass":
        if (pass) {
          setPassErr(authServiceFactory().checkPWRequirements(pass))
        } else {
          setPassErr("Enter a password.");
        }
        break;
      case "confirmpass":
        if (confirmpass && confirmpass !== pass) {
          setConfirmErr("Password mismatch.");
        } else if (!confirmpass) {
          setConfirmErr("Re-enter your password.")
        } else {
          setConfirmErr("");
        }
        break;
    }
  }

  function onKeyUp(event: React.KeyboardEvent) {
    if (event.key !== "Enter") { return; }
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    switch (propName) {
      case "email":
        passRef.current?.focus();
        break;
      case "pass":
        confirmRef.current?.focus();
        break;
      case "confirmpass":
        // TODO:
        break;
    }
  }

  return <div className="main-section-inner onboarding-welcome-container">
    <header className="welcome-heading">
      <div className="logo-img" />
    </header>
    <form className="form-wrapper capture-credentials-form">
      <h1 className="welcome-heading">We Hope You Enjoyed {productName}</h1>
      { finished ? <div className="success-message">
        <h2>All Set!</h2>
        <h3>You've finished setting up your account!</h3>
        <p>
          <span>Learn more about getting your own {productName} </span>
          <a href="https://sentriqs.com">here</a>
          <span>.</span>
        </p>
      </div> : <>
        <p>We strongly recommend that you create login credentials for your account.
          This account can be reused in the future with other workspaces or meetings
          to which you're invited.
        </p>
        {saveErr && <Alert severity="error">{saveErr}</Alert>}
        <div className="form-row">
          <FormControl fullWidth>
            <TextField id="email-input" label="Email" type="email" name="email"
              autoComplete="username" size="small"
              required
              error={!!emailErr}
              helperText={emailErr}
              variant="outlined"
              value={email}
              onChange={updateValue}
              onBlur={onBlur}
              onKeyUp={onKeyUp} />
          </FormControl>
        </div>
        <h2>Password:</h2>
        <div className="form-row">
          <FormControl fullWidth>
            <TextField id="pass-input" label="Create Password" name="pass"
              type="password" size="small"
              autoComplete="new-password"
              required
              error={!!passErr}
              helperText={passErr}
              variant="outlined"
              value={pass}
              inputRef={passRef}
              onChange={updateValue}
              onBlur={onBlur}
              onKeyUp={onKeyUp} />
          </FormControl>
        </div>
        <div className="form-row">
          <FormControl fullWidth>
            <TextField id="confirm-input" label="Confirm Password" name="confirmpass"
              autoComplete="new-password" size="small"
              required
              type="password"
              error={!!confirmErr}
              helperText={confirmErr}
              variant="outlined"
              value={confirmpass}
              inputRef={confirmRef}
              onChange={updateValue}
              onBlur={onBlur}
              onKeyUp={onKeyUp} />
          </FormControl>
        </div>
        <div className="password-rules">
          <p>Passwords must:</p>
          <ul>
            <li>be 10 characters or longer,</li>
            <li>contain one lower case letter,</li>
            <li>contain one capital letter,</li>
            <li>contain one number,</li>
            <li>contain one special character, or</li>
            <li>be 24 characters or longer</li>
          </ul>
        </div>
        <div className="button-row">
          <Button color="primary" variant="contained"
              disableRipple disableTouchRipple
              onClick={doSaveCredentials}>
            Save
          </Button>
        </div>
      </>}
    </form>
  </div>;
}
